import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "to", "href", "onClick", "use", "size", "isGhost"],
  _excluded2 = ["id", "endSlot", "completed", "completedMessage", "duration", "remainingTime", "footer", "description", "thumbnailSrc", "thumbnailOverlay", "thumbnailUse", "disabled", "name", "onClick", "to", "use", "size", "href", "className", "truncateDescription"];
import { Link } from 'react-router-dom';
import styled, { css, theme } from '../../utils/Styling';
import UIImage from 'UIComponents/image/UIImage';
import { useState } from 'react';
import ACSkeletonBox from '../loading/ACSkeletonBox';
import { H7 } from 'foundations-components/transitional/components/H7';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ACIcon from '../icon/ACIcon';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import I18n from 'I18n';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const linkStyles = css(["display:flex;min-height:var(--card-height);align-items:center;width:100%;border-radius:3px;gap:var(--card-gap);padding-right:8px;&:hover{background-color:var(--hover-background-color);box-shadow:var(--hover-background-color) 0px 0px 0px 4px;}color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;-webkit-font-smoothing:auto;:hover{color:inherit;font:inherit;line-height:inherit;text-decoration:inherit;}"]);
const CardDimensions = {
  'extra-small': 60,
  small: 70,
  medium: 100
};
const CardGap = {
  'extra-small': 12,
  small: 12,
  medium: 16
};
const InternalLink = styled(Link).withConfig({
  displayName: "ACInlineContentCard__InternalLink",
  componentId: "sc-1x5uv26-0"
})(["--hover-background-color:", ";--card-height:", "px;--card-gap:", "px;", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : props.use === 'on-surface-raised' ? theme.colors.KOALA // will migrate to trellis
: theme.colors.PANTERA, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ExternalLink = styled.a.withConfig({
  displayName: "ACInlineContentCard__ExternalLink",
  componentId: "sc-1x5uv26-1"
})(["--hover-background-color:", ";--card-height:", "px;--card-gap:", "px;", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : props.use === 'on-surface-raised' ? theme.colors.KOALA : theme.colors.PANTERA, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ClickableCard = styled.div.withConfig({
  displayName: "ACInlineContentCard__ClickableCard",
  componentId: "sc-1x5uv26-2"
})(["--hover-background-color:", ";--card-height:", "px;--card-gap:", "px;", ""], props => props.use === 'on-light' ? theme.colors.GYPSUM : props.use === 'on-surface-raised' ? theme.colors.KOALA : theme.colors.PANTERA, ({
  size
}) => CardDimensions[size], ({
  size
}) => CardGap[size], linkStyles);
const ThumbnailOverlayWrapper = styled.div.withConfig({
  displayName: "ACInlineContentCard__ThumbnailOverlayWrapper",
  componentId: "sc-1x5uv26-3"
})(["position:absolute;height:100%;width:100%;", ""], ({
  $isGhost
}) => $isGhost && css(["&::after{content:'';display:block;position:absolute;top:0;right:0;bottom:0;left:0;background-color:", ";opacity:0.6;z-index:1;pointer-events:none;}"], theme.colors.FLINT));
const StartSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__StartSlot",
  componentId: "sc-1x5uv26-4"
})(["position:relative;height:100%;width:var(--card-height);"]);
const ThumbnailWrapper = styled.div.withConfig({
  displayName: "ACInlineContentCard__ThumbnailWrapper",
  componentId: "sc-1x5uv26-5"
})(["position:relative;border-radius:3px;overflow:hidden;min-width:var(--card-height);aspect-ratio:1 / 1;"]);
const MiddleSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__MiddleSlot",
  componentId: "sc-1x5uv26-6"
})(["display:flex;justify-content:space-between;flex-direction:column;flex:1;", " color:", ";"], props => props.size === 'medium' && 'gap: 4px;', ({
  $isGhost
}) => $isGhost ? theme.colors.FLINT : 'inherit');
const EndSlot = styled.div.withConfig({
  displayName: "ACInlineContentCard__EndSlot",
  componentId: "sc-1x5uv26-7"
})(["display:flex;align-items:center;"]);
const CardCompletedMessage = ({
  completedMessage = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.progress.completed"
  })
}) => {
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    gap: 4,
    children: [/*#__PURE__*/_jsx(ACIcon, {
      color: theme.colors.CALYPSO,
      name: "checkmark"
    }), completedMessage]
  });
};
const ContentCardLink = _ref => {
  let {
      children,
      to,
      href,
      onClick = () => {},
      use = 'on-light',
      size = 'small',
      isGhost
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (!isGhost) {
    if (to) {
      return /*#__PURE__*/_jsx(InternalLink, Object.assign({
        onClick: onClick,
        size: size,
        to: to,
        use: use
      }, rest, {
        children: children
      }));
    }
    if (href) {
      return /*#__PURE__*/_jsx(ExternalLink, Object.assign({
        href: href,
        onClick: onClick,
        size: size,
        use: use
      }, rest, {
        children: children
      }));
    }
  }
  return /*#__PURE__*/_jsx(ClickableCard, Object.assign({
    onClick: onClick,
    role: "button",
    size: size,
    use: use
  }, rest, {
    children: children
  }));
};
const ACInlineContentCard = _ref2 => {
  let {
      id,
      endSlot,
      completed,
      completedMessage,
      duration,
      remainingTime,
      footer,
      description,
      thumbnailSrc,
      thumbnailOverlay,
      thumbnailUse,
      disabled = false,
      name,
      onClick = () => {},
      to,
      use = 'on-light',
      size = 'small',
      href,
      className,
      truncateDescription = true
    } = _ref2,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const progress = completed ? 1 : duration && remainingTime ? (duration - remainingTime) / duration : 0;
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const thumbnailLoading = thumbnailSrc && !thumbnailLoaded;
  return /*#__PURE__*/_jsxs(ContentCardLink, Object.assign({
    className: `inline-content-card ${className}`,
    href: href,
    isGhost: disabled,
    onClick: onClick,
    size: size,
    to: to,
    use: use
  }, props, {
    children: [/*#__PURE__*/_jsx(StartSlot, {
      children: /*#__PURE__*/_jsxs(ThumbnailWrapper, {
        children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
          style: {
            position: 'absolute',
            height: '100%',
            width: '100%'
          }
        }), thumbnailSrc ? /*#__PURE__*/_jsx(UIImage, {
          alt: I18n.text('academyCommon.contentCard.thumbnailAlt', {
            title: name
          }),
          loading: "lazy",
          onLoad: () => setThumbnailLoaded(true),
          src: thumbnailSrc,
          style: {
            visibility: thumbnailLoading ? 'hidden' : 'visible',
            position: 'absolute',
            height: '100%',
            filter: thumbnailUse === 'dark' ? 'brightness(0.5)' : undefined
          }
        }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
          seed: id,
          style: {
            position: 'absolute',
            height: '100%'
          },
          title: name,
          type: "square"
        }), /*#__PURE__*/_jsxs(ThumbnailOverlayWrapper, {
          $isGhost: disabled,
          children: [thumbnailOverlay, /*#__PURE__*/_jsx("div", {
            style: {
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: 3
            },
            children: /*#__PURE__*/_jsx(UINanoProgress, {
              "aria-label": I18n.text('academyCommon.contentCard.progressBar'),
              color: progress === 1 ? theme.colors.CALYPSO : theme.colors.SORBET,
              height: 3,
              value: progress * 100
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsxs(MiddleSlot, {
      $isGhost: disabled,
      size: size,
      children: [size === 'small' || size === 'extra-small' ? /*#__PURE__*/_jsx(H7, {
        marginBlockEnd: "100",
        children: name
      }) : /*#__PURE__*/_jsx(Text, {
        as: "h2",
        marginBlockEnd: "0",
        typography: "heading-200",
        children: name
      }), description && (truncateDescription ? /*#__PURE__*/_jsx(TruncateString, {
        children: description
      }) : description), completed && /*#__PURE__*/_jsx(CardCompletedMessage, {
        completedMessage: completedMessage
      }), footer]
    }), /*#__PURE__*/_jsx(EndSlot, {
      children: endSlot
    })]
  }));
};
export default ACInlineContentCard;